<template>
    <div v-if="visible" class="canvas-wrapper" :class="{ 'exam-mode': examMode, 'light-canvas': isLightCanvas && !examMode }">
        <div class="canvas-container">
            <canvas ref="bgCanvas" class="hand-input-bg"></canvas>
            <canvas ref="canvas" class="hand-input"></canvas>
        </div>
        <div class="button-column">
            <button
                @click="toggleEraser"
                class="canvas-btn"
                :class="{ active: isErasing }"
            >
                <span class="btn-icon">
                    <Brush v-if="isErasing" :size="20" />
                    <Eraser v-else :size="20" />
                </span>
                <span class="btn-label">{{ isErasing ? "Draw" : "Erase" }}</span>
            </button>
            <button @click="handleClear" class="canvas-btn destructive">
                <span class="btn-icon">
                    <Trash2 :size="20" />
                </span>
                <span class="btn-label">Clear</span>
            </button>
            <button @click="handleSave" class="canvas-btn">
                <span class="btn-icon">
                    <Save :size="20" />
                </span>
                <span class="btn-label">Save</span>
            </button>
            <button
                @click="toggleLines"
                class="canvas-btn"
                :class="{ active: ruleLines }"
            >
                <span class="btn-icon">
                    <Ruler :size="20" />
                </span>
                <span class="btn-label">Lines</span>
            </button>
        </div>
    </div>
</template>

<script setup>
import { ref, watch, onMounted, onUnmounted, nextTick, computed } from 'vue';
import { useAuthStore } from '@/stores/auth';
import { Brush, Eraser, Trash2, Save, Ruler } from 'lucide-vue-next';

const props = defineProps({
    visible: Boolean,
    savedData: String
});

const emit = defineEmits(['save', 'update:savedData']);

const authStore = useAuthStore();
// Exam mode requires Tier 2+ subscription
const examMode = computed(() => {
    const hasTier = authStore.hasTierAccess('TIER 2');
    const isEnabled = authStore.settings?.appearance?.exam_mode ?? false;
    return hasTier && isEnabled;
});
const theme = computed(() => authStore.settings?.appearance?.theme ?? 'dark');

// Light mode = exam mode OR light theme
const isLightCanvas = computed(() => {
    if (examMode.value) return true;
    if (theme.value === 'light') return true;
    if (theme.value === 'auto') {
        // Check system preference
        return window.matchMedia?.('(prefers-color-scheme: light)').matches ?? false;
    }
    return false;
});

// Colors based on light/dark canvas
const bgColor = computed(() => isLightCanvas.value ? '#ffffff' : '#000000');
const strokeColor = computed(() => isLightCanvas.value ? '#000000' : '#ffffff');
const lineColor = computed(() => isLightCanvas.value ? '#cccccc' : '#444444');

const canvas = ref(null);
const bgCanvas = ref(null);
const isErasing = ref(false);
const ruleLines = ref(false);
const lineSpacing = 50;

let isDrawing = false;
let lastX = 0;
let lastY = 0;
let resizeObserver = null;
let activePointerIds = new Set();

const initCanvas = () => {
    const canvasEl = canvas.value;
    const bgCanvasEl = bgCanvas.value;
    
    if (!canvasEl || !bgCanvasEl) return;

    const rect = canvasEl.getBoundingClientRect();

    // Setup background canvas
    bgCanvasEl.width = rect.width;
    bgCanvasEl.height = rect.height;
    const bgCtx = bgCanvasEl.getContext('2d');

    bgCtx.fillStyle = bgColor.value;
    bgCtx.fillRect(0, 0, bgCanvasEl.width, bgCanvasEl.height);

    if (ruleLines.value) {
        bgCtx.strokeStyle = lineColor.value;
        bgCtx.lineWidth = 1;
        for (let y = lineSpacing; y < bgCanvasEl.height; y += lineSpacing) {
            bgCtx.beginPath();
            bgCtx.moveTo(0, y);
            bgCtx.lineTo(bgCanvasEl.width, y);
            bgCtx.stroke();
        }
    }

    // Setup drawing canvas
    canvasEl.width = rect.width;
    canvasEl.height = rect.height;
    const ctx = canvasEl.getContext('2d');
    ctx.clearRect(0, 0, canvasEl.width, canvasEl.height);

    // Restore saved drawing
    if (props.savedData) {
        const img = new Image();
        img.onload = () => {
            ctx.drawImage(img, 0, 0);
        };
        img.src = props.savedData;
    }

    // Pointer events (handles mouse, touch, and stylus)
    canvasEl.onpointerdown = (e) => {
        isDrawing = true;
        canvasEl.setPointerCapture(e.pointerId);
        activePointerIds.add(e.pointerId);
        const r = canvasEl.getBoundingClientRect();
        lastX = e.clientX - r.left;
        lastY = e.clientY - r.top;
    };

    canvasEl.onpointermove = (e) => {
        if (!isDrawing) return;

        const r = canvasEl.getBoundingClientRect();
        const x = e.clientX - r.left;
        const y = e.clientY - r.top;

        if (isErasing.value) {
            const eraserSize = 20;
            ctx.globalCompositeOperation = 'destination-out';
            ctx.beginPath();
            ctx.arc(x, y, eraserSize / 2, 0, Math.PI * 2);
            ctx.fill();
            ctx.globalCompositeOperation = 'source-over';
        } else {
            ctx.strokeStyle = strokeColor.value;
            ctx.lineWidth = 2;
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
            ctx.beginPath();
            ctx.moveTo(lastX, lastY);
            ctx.lineTo(x, y);
            ctx.stroke();
        }

        lastX = x;
        lastY = y;
    };

    canvasEl.onpointerup = (e) => {
        isDrawing = false;
        canvasEl.releasePointerCapture(e.pointerId);
        activePointerIds.delete(e.pointerId);
    };
    canvasEl.onpointerleave = (e) => {
        isDrawing = false;
        if (canvasEl.hasPointerCapture(e.pointerId)) {
            canvasEl.releasePointerCapture(e.pointerId);
            activePointerIds.delete(e.pointerId);
        }
    };
    canvasEl.onpointercancel = (e) => {
        isDrawing = false;
        if (canvasEl.hasPointerCapture(e.pointerId)) {
            canvasEl.releasePointerCapture(e.pointerId);
            activePointerIds.delete(e.pointerId);
        }
    };

    // Keep canvas bitmap in sync when the container resizes
    if (!resizeObserver) {
        resizeObserver = new ResizeObserver(() => {
            if (!canvas.value || !bgCanvas.value) return;

            const rect = canvas.value.getBoundingClientRect();
            const w = rect.width | 0;
            const h = rect.height | 0;
            if (canvas.value.width === w && canvas.value.height === h) return;
            if (w === 0 || h === 0) return;

            const ctx = canvas.value.getContext('2d');
            const pixels = ctx.getImageData(0, 0, canvas.value.width, canvas.value.height);

            bgCanvas.value.width = w;
            bgCanvas.value.height = h;
            canvas.value.width = w;
            canvas.value.height = h;

            ctx.putImageData(pixels, 0, 0);
            redrawBackground();
        });
        resizeObserver.observe(canvasEl.parentElement);
    }
};

const handleClear = () => {
    const canvasEl = canvas.value;
    if (canvasEl) {
        const ctx = canvasEl.getContext('2d');
        ctx.clearRect(0, 0, canvasEl.width, canvasEl.height);
    }
};

const handleSave = () => {
    const canvasEl = canvas.value;
    const bgCanvasEl = bgCanvas.value;

    if (canvasEl && bgCanvasEl) {
        // Create a composite canvas to include the background
        const compositeCanvas = document.createElement('canvas');
        compositeCanvas.width = canvasEl.width;
        compositeCanvas.height = canvasEl.height;
        const compositeCtx = compositeCanvas.getContext('2d');

        // Draw background first (includes the proper bg color)
        compositeCtx.drawImage(bgCanvasEl, 0, 0);

        // Draw the user's drawing on top
        compositeCtx.drawImage(canvasEl, 0, 0);

        // Export the composite image
        const thumbnail = compositeCanvas.toDataURL('image/webp');
        emit('save', thumbnail);
        handleClear();
    }
};

const redrawBackground = () => {
    const bgCanvasEl = bgCanvas.value;
    if (!bgCanvasEl) return;

    const bgCtx = bgCanvasEl.getContext('2d');

    bgCtx.fillStyle = bgColor.value;
    bgCtx.fillRect(0, 0, bgCanvasEl.width, bgCanvasEl.height);

    if (ruleLines.value) {
        bgCtx.strokeStyle = lineColor.value;
        bgCtx.lineWidth = 1;
        for (let y = lineSpacing; y < bgCanvasEl.height; y += lineSpacing) {
            bgCtx.beginPath();
            bgCtx.moveTo(0, y);
            bgCtx.lineTo(bgCanvasEl.width, y);
            bgCtx.stroke();
        }
    }
};

const toggleEraser = () => {
    isErasing.value = !isErasing.value;
};

const toggleLines = () => {
    ruleLines.value = !ruleLines.value;
    redrawBackground();
};

watch(() => props.visible, async (newVal) => {
    if (!newVal) {
        // Save before hiding
        const canvasEl = canvas.value;
        if (canvasEl) {
            // Release any active pointer captures
            activePointerIds.forEach(pointerId => {
                if (canvasEl.hasPointerCapture(pointerId)) {
                    canvasEl.releasePointerCapture(pointerId);
                }
            });
            activePointerIds.clear();
            isDrawing = false;

            const data = canvasEl.toDataURL('image/png');
            emit('update:savedData', data);
        }
        if (resizeObserver) {
            resizeObserver.disconnect();
            resizeObserver = null;
        }
    } else {
        // Restore after showing
        await nextTick();
        await new Promise(r => setTimeout(r, 0));
        initCanvas();
    }
});

onMounted(() => {
    if (props.visible) {
        initCanvas();
    }
});

onUnmounted(() => {
    // Release any active pointer captures before unmounting
    const canvasEl = canvas.value;
    if (canvasEl) {
        activePointerIds.forEach(pointerId => {
            if (canvasEl.hasPointerCapture(pointerId)) {
                canvasEl.releasePointerCapture(pointerId);
            }
        });
        activePointerIds.clear();
    }

    if (resizeObserver) {
        resizeObserver.disconnect();
        resizeObserver = null;
    }
});

defineExpose({
    initCanvas,
    clearCanvas: handleClear
});
</script>

<style scoped>
.canvas-wrapper {
    display: flex;
    gap: 6px;
    align-items: stretch;
    max-width: 100%;
    overflow: hidden;
}

.canvas-container {
    position: relative;
    flex: 1;
    min-width: 0;
    height: 500px;
}

.hand-input-bg,
.hand-input {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: 2px solid var(--accent);
    box-sizing: border-box;
    touch-action: none;
}

.hand-input-bg {
    pointer-events: none;
}

.button-column {
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    width: 52px;
}

.canvas-btn {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 12px 8px;
    background: rgba(19, 19, 19, 0.6);
    border: 2px solid var(--accent);
    color: var(--text-primary);
    cursor: pointer;
    transition: all 0.2s;
    font-size: 12px;
    flex: 1;
}

.canvas-btn:hover:not(:disabled) {
    background: var(--bg-secondary);
    border-color: var(--accent);
    transform: scale(0.97);
}

.canvas-btn.active {
    background: rgba(254, 231, 179, 0.15);
    border-color: var(--accent);
}

.canvas-btn.destructive:hover:not(:disabled) {
    border-color: #ff6b6b;
    background: rgba(255, 107, 107, 0.1);
}

.btn-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--text-primary);
}

.btn-label {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    white-space: nowrap;
}

/* Exam mode styles */
.canvas-wrapper.exam-mode .hand-input-bg,
.canvas-wrapper.exam-mode .hand-input {
    border: 1px solid #000000;
}

.canvas-wrapper.exam-mode .canvas-btn {
    background: #ffffff;
    border: 1px solid #000000;
    color: #000000;
    font-family: "Times New Roman", Times, serif;
}

.canvas-wrapper.exam-mode .canvas-btn:hover:not(:disabled) {
    background: #f0f0f0;
    border-color: #000000;
}

.canvas-wrapper.exam-mode .canvas-btn.active {
    background: #e0e0e0;
    border-color: #000000;
}

.canvas-wrapper.exam-mode .canvas-btn.destructive:hover:not(:disabled) {
    border-color: #000000;
    background: #f0f0f0;
}

.canvas-wrapper.exam-mode .btn-icon {
    color: #000000;
}

/* Light theme (non-exam) - keeps accent colors */
.canvas-wrapper.light-canvas .canvas-btn {
    background: var(--bg-secondary);
    color: var(--text-primary);
}

.canvas-wrapper.light-canvas .canvas-btn:hover:not(:disabled) {
    background: var(--bg-primary);
}

.canvas-wrapper.light-canvas .canvas-btn.active {
    background: rgba(var(--accent-rgb), 0.15);
}
</style>